// The screens' calls to charges.mts (getAuthHeaders, never a hand-built key) and the messages of its codes.
import { getAuthHeaders } from './globals.js';

export const CHARGE_MESSAGES = {
  forbidden: 'Solo la administración de Oike registra cobros.',
  payer_required: 'Elegí quién paga: una persona o una empresa.',
  person_required: 'Elegí la persona que paga.',
  company_required: 'Elegí la empresa que paga.',
  company_only_reference: 'Si paga la persona, su empresa solo puede ir como referencia.',
  company_is_payer: 'Si paga la empresa, no es una referencia: es la pagadora.',
  beneficiary_only_company: 'La persona beneficiaria se indica cuando paga una empresa.',
  quote_required: 'Falta la versión de la cotización.',
  quote_not_accepted: 'Solo una propuesta aceptada por el cliente genera una cuenta por cobrar (aprobarla o publicarla no alcanza).',
  payer_mismatch: 'Ese cobro es de otro pagador: un pago se aplica solo a cobros de quien paga.',
  currency_mismatch: 'La moneda del pago y la del cobro deben coincidir (no se convierte).',
  over_charge: 'Eso supera lo pendiente del cobro.',
  over_payment: 'Lo aplicado supera el importe del pago.',
  invalid_allocations: 'Revisá qué cobros cubre el pago.',
  charge_not_found: 'No se encontró uno de los cobros.',
  invalid_day: 'La fecha no es válida.',
  future_day: 'La fecha no puede ser futura.',
  payment_not_confirmed: 'El pago no está confirmado.',
  over_refund: 'La devolución supera lo que queda del pago.',
  over_refund_charge: 'Eso supera lo aplicado de ese pago a ese cobro.',
  refund_needs_charges: 'Indicá de qué cobros sale la devolución (el pago no tiene dinero sin aplicar suficiente).',
  refund_inconsistent: 'La devolución quedó pendiente de revisión: otro registro cambió los importes al mismo tiempo.',
  payment_has_refunds: 'El pago tiene devoluciones: no se anula.',
  refund_application: 'Una devolución se corrige anulándola, no revirtiendo su aplicación.',
  earlier_plan_unresolved: 'Una versión anterior tiene cobros pendientes: resolvé si siguen vigentes o se cancelan antes de generar el nuevo plan.',
  paid_above_agreed: 'Ya se cobró más que el total de esta versión: resolvé la diferencia antes de generar un plan (no se generan importes negativos ni devoluciones automáticas).',
  not_earlier_charge: 'Ese cobro no es de una versión anterior de esta propuesta.',
  document_pending: 'El comprobante todavía no se emitió.',
  invalid_email: 'Revisá el email del destinatario.',
  subject_required: 'Escribí el asunto.',
  send_uncertain: 'Un envío anterior quedó sin respuesta de Gmail. Revisá Enviados en Gmail antes de intentar de nuevo.',
  gmail_not_connected: 'Gmail no está conectado en Configuración.',
  gmail_failed: 'Gmail no confirmó el envío. El pago y el recibo siguen guardados: podés reintentarlo.',
  test_mode: 'En el entorno de prueba no se envían correos.',
  invalid_issuer_email: 'El email del emisor no es válido.',
  invalid_color: 'Un color no es válido (formato #RRGGBB).',
  invalid_font: 'Elegí una de las tipografías disponibles.',
  number_conflict: 'No se pudo reservar un número único: volvé a intentar la emisión.',
  plan_exceeds_balance: 'El plan supera lo que queda por cobrar de esta propuesta (ya descontados los pagos anteriores y los cobros que siguen vigentes). Si es una contratación adicional independiente, marcala como tal.',
  additional_reason_required: 'Explicá por qué es una contratación adicional independiente (10 caracteres o más).',
  additional_only_quote: 'Una contratación adicional se marca sobre una propuesta.',
  plan_not_recoverable: 'Ese plan no guarda su definición: completalo a mano.',
  quote_recipient_unknown: 'El destinatario de esa cotización está por revisar: aclaralo antes de cobrar.',
  quote_payer_mismatch: 'El pagador debe ser el destinatario de la cotización.',
  quote_currency_mismatch: 'La moneda debe ser la de la cotización.',
  direct_without_quote: 'Un cobro directo no lleva cotización.',
  concept_required: 'Escribí el concepto.',
  invalid_currency: 'La moneda va como código de 3 letras (USD, EUR, ARS…).',
  invalid_plan: 'El plan debe ser: pago completo, anticipo y saldo, o dos o más cuotas.',
  invalid_amount: 'Cada importe debe ser mayor que 0, con hasta 2 decimales.',
  invalid_due: 'Cada vencimiento necesita un día válido.',
  key_required: 'Volvé a intentarlo.',
  link_not_found: 'No se encontró la persona, la empresa o la cotización.',
  single_link: 'Cada cobro va con una sola persona, empresa o cotización.',
  charge_cancelled: 'El cobro está cancelado.',
  charge_has_applications: 'El cobro ya tiene pagos aplicados: no se edita ni se cancela; se revierten antes.',
  reason_required: 'Escribí el motivo (10 caracteres o más).',
  confirm_required: 'Confirmá antes de guardar.',
  no_change: 'No hay cambios.',
  corrections_field_missing: 'Falta el campo «Receipt Corrections JSON» en Airtable. No se cambió nada.',
  no_next_installment: 'No hay una cuota siguiente de este plan: cobrá el resto en otra fecha o dejalo pendiente en esta cuota.',
  invalid_rest_mode: 'Elegí qué pasa con el resto.',
  read_failed: 'No se pudieron leer los cobros. Probá de nuevo.',
  write_failed: 'No se pudo guardar. Probá de nuevo: no se duplica.',
};

export async function chargesCall(action, body = {}) {
  const res = await fetch('/api/charges', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ action, ...body }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(CHARGE_MESSAGES[data.error] || `No se pudo completar (${data.error || res.status}).`);
  return data;
}

/** A creation key for one form submission: the same key on every retry of that submission. */
export const newChargeKey = () => `ch-${(globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9-]/g, '').slice(0, 40)}`;

/** Downloads a PDF the server built from a frozen document (base64). */
export function downloadPdf(filename, base64) {
  const bin = atob(base64); const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
